import axios from 'axios';
import { getCookie, deleteCookie } from '../../utils/common';
import { env } from '../../utils/env';
import { getAppId } from '../../constants/appIdentity';
import {
  ACCEPT_ENCRYPTED_HEADER,
  ENCRYPTED_FIELDS_HEADER,
  ENCRYPTED_PROBE_HEADER,
  decryptResponseFields,
  encryptRequestFields,
  noteProbeResult,
  parseEncryptedFields,
  sensitiveFieldsForUrl,
  shouldAcceptEncrypted,
} from '../../utils/transitPayload';

const API_BASE_URL = env('REACT_APP_API_BASE_URL') || 'http://localhost:8000/api';
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
});

console.log(API_BASE_URL);
axiosInstance.interceptors.request.use(
  (config) => {
    try {
      // NOTE: '/login/set-password' is NOT here any more. It used to be an
      // unauthenticated endpoint that took a username and a new password — anyone
      // who knew a username could seize the account. It now requires a session and
      // only changes the caller's OWN password, so it must carry the bearer token.
      // Forgotten passwords go through /login/forgot-password + /login/reset-password.
      const noAuthRequired = [
        '/login/new-account',
        '/login/check-password',
        '/login/forgot-password',
        '/login/reset-password',
      ];

      const skipAuth = noAuthRequired.some((endpoint) =>
        config.url?.includes(endpoint)
      );

      // Which surface this bundle is. The backend prefers the browser's Origin
      // header (which page JS cannot forge) and uses this only as a fallback, so
      // it is a routing hint rather than anything security-bearing.
      //
      // Omitted entirely when unset (local dev), which the backend reads as
      // 'no filtering' — an empty header string would be ambiguous.
      const appId = getAppId();
      if (appId) config.headers['X-App-Id'] = appId;

      // Payload-level encryption, on top of TLS. Declaring support here means the server encrypts
      // PII in the response and names it in X-Encrypted-Fields; the response interceptor below puts
      // it back before any caller sees it. Opt-in per client, so a build without this header is
      // served exactly what it is served today — including this bundle when no key is configured,
      // or when a response has already proved the two keys disagree.
      if (shouldAcceptEncrypted()) config.headers[ACCEPT_ENCRYPTED_HEADER] = '1';

      // Credentials go out encrypted. Done centrally rather than in each service so a caller
      // cannot forget: every request to a credential-bearing endpoint is covered by URL.
      const sensitiveFields = sensitiveFieldsForUrl(config.url);
      if (sensitiveFields && config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
        config.data = encryptRequestFields(config.data, sensitiveFields);
      }

      if (!skipAuth) {
        const accessToken = getCookie('accessToken');
        if (accessToken) {
          const authHeader = `Bearer ${accessToken}`; 
          config.headers['Authorization'] = authHeader;
          
          //console.log('Adding Authorization header:', authHeader); 
          
        }
      }

      return config;
    } catch (error) {
      console.error("Error in request interceptor:", error);
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Undo the server's field encryption before the payload reaches application code, so nothing
    // downstream — filters, sorts, forms that round-trip a value — has to know this happened.
    let data = response.data;
    try {
      const fields = parseEncryptedFields(response.headers?.[ENCRYPTED_FIELDS_HEADER]);
      if (fields) {
        // Only trust what we decrypt once the server's probe proves we hold the same key.
        if (noteProbeResult(response.headers?.[ENCRYPTED_PROBE_HEADER])) {
          data = decryptResponseFields(response.data, fields);
        }
      }
    } catch (e) {
      // Never lose a response over this; render what arrived.
      console.error('Failed to decrypt response fields:', e);
    }
    // Opt-in escape hatch: this interceptor normally unwraps to `data`, which makes response
    // headers unreachable. Some endpoints carry a row count alongside a list body — the resale
    // units roster reports how many match the search in X-Total-Count, because its body is a bare
    // array other callers already consume. Callers that need it set `withHeaders` on the request
    // and get `{ data, headers }`; every existing caller is untouched.
    if (response.config?.withHeaders) {
      return { data, headers: response.headers };
    }
    return data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true; 
      console.log("401 error detected, attempting token refresh/logout");

      
      const refreshToken = getCookie('refreshToken'); 
      if (!refreshToken) {
        console.log("No refresh token found, logging out.");
        
        window.dispatchEvent(new Event('forceLogout'));
        return Promise.reject(error);  
      } else {
        
      }
    }

    console.error("API Error:", error);
    return Promise.reject(error);
  }
);



export default axiosInstance;